/* eslint-disable @typescript-eslint/triple-slash-reference */

import * as React from 'react';

import { ITargetsParams, ITargetsResponse } from '@cian/adfox/build/api';
import {
  calculatedParamsMiddleware,
  IAdFoxBannerTypeParams,
  IAdFoxParams,
  IAdFoxTargets,
  mergeMiddlewares,
} from '@cian/adfox/build/middlewares';
import { AdFoxBanner, IAdFoxBannerContext } from '@cian/adfox/build/react';
import { LazyLoad } from '@cian/components';
import { IOfferSchema } from '@cian/api-models/offer-card/v1/get-offer-data';

import { IMakeRequest } from '../../utils/make_request';
import { sendLogStatsBanner } from '../../api/banners';
import { getRandomId } from '../../utils/banners';
import { FDealType, getDealType } from '../../utils/deal_type';
import { AdFoxBannerType, getBannerByType, offerRequest, BANNER_TEMPLATE_TYPE } from './templates';
import { ContractBanner } from './templates/contract';
import { MortgageBannerContainer } from './templates/mortgage';
import { TgbBanner } from './templates/tgb';

import { getSpecialPriceTargetPuidForAdFox } from '../../utils/adfox';
import { MortgageOfferCardBanner } from './templates/mortgage_offer_card_banner';
import { OfferCardImageBannerContainer } from './templates/offer_card_image_banner';
import { MortgageInRentContainer } from './templates/mortgage_in_rent';
import { StretchWithTGB } from './templates/StretchWithTGB/StretchWithTGB';
import { AdFoxBanner as AdFoxBannerNew } from '../AdFoxBanner';
import { EAdFoxBannerType } from '@cian/adfox-component';

const PR_RANDOM = getRandomId();

export interface IAddAdFoxBannerParameters {
  type: AdFoxBannerType;
  key: string | number;
  makeRequest: IMakeRequest;
  offer: IOfferSchema;
  randomId: string;
  positionInfo: string;
  className?: string;
  additionalPuids?: { [key: string]: string | undefined };
  isAgent: boolean;
  isNew?: boolean;
}

export function addAdFoxBanner(parameters: IAddAdFoxBannerParameters): JSX.Element {
  const {
    type,
    key,
    makeRequest,
    offer,
    randomId,
    positionInfo,
    className,
    additionalPuids,
    isAgent,
    isNew,
  } = parameters;

  const dealType = getDealType(offer);

  const middleware = () =>
    bannerMiddleware(
      type,
      offerRequest({
        dealType: dealType === FDealType.Sale ? 'sale' : 'rent',
        offerId: offer.cianId.toString(),
        offerType: offer.offerType,
      }).then((res: ITargetsResponse) => {
        const now = new Date();
        const dl = encodeURIComponent(document.location.toString());

        return Object.assign({}, res.body.data.params, {
          dl,
          pd: now.getDate(),
          pr: PR_RANDOM,
          pr1: randomId,
          pv: now.getHours(),
          pw: now.getDay(),
          ...(additionalPuids || {}),
          puid6: getSpecialPriceTargetPuidForAdFox(offer.bargainTerms.prices.rur),
        });
      }),
    );

  return (
    <aside key={randomId} className={className || ''}>
      {getBanner({ key, makeRequest, middleware, positionInfo, additionalPuids, isAgent, isNew })}
      {/* Пустой div для места РСЯ баннера (РСЯ баннер может быть вместо любого баннера) */}
      {!isAgent && <div id={`AdFox_banner_${randomId}`} />}
    </aside>
  );
}

interface IGetBannerParameters {
  key: string | number;
  makeRequest: IMakeRequest;
  middleware(): Promise<IAdFoxParams>;
  positionInfo: string;
  additionalPuids?: { [key: string]: string | undefined };
  isNew?: boolean;
  isAgent: boolean;
}

function getBanner(parameters: IGetBannerParameters): React.ReactNode {
  const { key, makeRequest, middleware, positionInfo, additionalPuids, isNew, isAgent } = parameters;

  if (isNew) {
    return (
      <LazyLoad
        key={key}
        offset={300}
        onLoad={middleware}
        renderLoaded={(result: IAdFoxParams) => {
          const { pr, pr1, ...rest } = result;

          return (
            <AdFoxBannerNew
              type={EAdFoxBannerType.Async}
              ownerId={202100}
              disableYan={isAgent}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              params={rest as any}
            />
          );
        }}
        renderEmpty={() => {
          return <div />;
        }}
      />
    );
  }

  return (
    <LazyLoad
      key={key}
      offset={300}
      onLoad={middleware}
      renderLoaded={(result: IAdFoxParams) => (
        <AdFoxBanner
          middleware={() => Promise.resolve(result)}
          renderFetched={(context: IAdFoxBannerContext) => {
            switch (context['type'] as BANNER_TEMPLATE_TYPE) {
              case 'mortgage':
                return <MortgageBannerContainer context={context} />;
              case 'seo':
                return <TgbBanner context={context} />;
              case 'stretch':
                return <StretchWithTGB context={context} />;
              case 'mortgage_offer_card_banner':
                return <MortgageOfferCardBanner context={context} />;
              case 'offer_card_image_banner':
                return <OfferCardImageBannerContainer context={context} />;
              case 'mortgage_in_rent':
                return <MortgageInRentContainer context={context} />;
              case 'contract':
                const monthlyPayment = additionalPuids && additionalPuids['monthlyPayment'];
                const mortgagePrice = monthlyPayment ? parseInt(monthlyPayment, 10) : undefined;

                return <ContractBanner monthlyPayment={mortgagePrice} context={context} />;
              default:
                return <div />;
            }
          }}
          renderFailure={error => {
            let errorType = 'template-error';

            if (error.message === 'Impossible to load script') {
              errorType = !window._cianCanRunAds ? 'request-error' : 'adblock-error';
            }

            sendLogStatsBanner(makeRequest, errorType, positionInfo);

            return <div />;
          }}
        />
      )}
      renderEmpty={() => {
        return <div />;
      }}
    />
  );
}

function bannerMiddleware(
  bannerType: AdFoxBannerType,
  request: Promise<IAdFoxTargets>,
  targetParams: ITargetsParams = {},
): Promise<IAdFoxParams> {
  return mergeMiddlewares(request, typeParamsMiddleware(bannerType), calculatedParamsMiddleware());
}

function typeParamsMiddleware(type: AdFoxBannerType): Promise<IAdFoxBannerTypeParams> {
  return Promise.resolve(getBannerByType(type));
}
